import {
  EvictorAdvancedEvictionConfigEntry,
  EvictorLabelSelectorExpression,
  EvictorLabelSelectorOperator,
} from '@cast/types-workload-eviction';
import {
  BuilderRule,
  EvictionConfigSettingKey,
  PresetType,
  PresetValues,
  RuleType,
} from './_types';

export const FIELD_NAMES = {
  enabled: 'enabled',
  aggressiveMode: 'aggressiveMode',
  scopedMode: 'scopedMode',
  ignorePodDisruptionBudgets: 'ignorePodDisruptionBudgets',
  nodeGracePeriodMinutes: 'nodeGracePeriodMinutes',
  cycleIntervalSeconds: 'cycleIntervalSeconds',
  maxTargetNodesPerCycle: 'maxTargetNodesPerCycle',
  advancedEvictorConfig: 'advancedEvictorConfig',
} as const;

export const PRESET_TYPES = [
  'cost-saving',
  'balanced',
  'performance-first',
  'custom',
] as const;

export const PRESET_VALUES: Record<
  Exclude<PresetType, 'custom'>,
  PresetValues
> = {
  'cost-saving': {
    cycleIntervalSeconds: 1 * 60,
    maxTargetNodesPerCycle: 40,
    nodeGracePeriodMinutes: 1,
    scopedMode: false,
    aggressiveMode: true,
  },
  balanced: {
    cycleIntervalSeconds: 5 * 60,
    maxTargetNodesPerCycle: 20,
    nodeGracePeriodMinutes: 5,
    scopedMode: false,
    aggressiveMode: false,
  },
  'performance-first': {
    cycleIntervalSeconds: 15 * 60,
    maxTargetNodesPerCycle: 5,
    nodeGracePeriodMinutes: 15,
    scopedMode: true,
    aggressiveMode: false,
  },
};

export const PRESET_TITLES: Record<PresetType, string> = {
  'cost-saving': 'Cost saving',
  balanced: 'Balanced',
  'performance-first': 'Performance-first',
  custom: 'Custom',
};

export const KINDS = ['ReplicaSet', 'StatefulSet', 'Job'] as const;

export const EVICTION_CONFIG_SETTING_KEYS = [
  'disposable',
  'removalDisabled',
  'aggressive',
] as const satisfies readonly EvictionConfigSettingKey[];

export const LABEL_OPERATORS = [
  'IN',
  'NOT_IN',
  'EXISTS',
  'DOES_NOT_EXIST',
] as const satisfies readonly EvictorLabelSelectorOperator[];

export const EMPTY_LABEL: EvictorLabelSelectorExpression = {
  key: '',
  operator: 'IN',
  values: [],
};

type RulePreset = {
  settingKey: keyof EvictorAdvancedEvictionConfigEntry['settings'];
  enabled: boolean;
};

export const RULE_PRESET_BY_TYPE: Record<RuleType, RulePreset> = {
  'evict-pod': { settingKey: 'disposable', enabled: true },
  'evict-node': { settingKey: 'disposable', enabled: true },
  'protect-pod': { settingKey: 'removalDisabled', enabled: true },
  'protect-node': { settingKey: 'removalDisabled', enabled: true },
  'aggressive-pod': { settingKey: 'aggressive', enabled: true },
  'aggressive-node': { settingKey: 'aggressive', enabled: true },
};

export const DEFAULT_RULE: BuilderRule = {};

export const MIN_REPLICAS = 1;

export const LABEL_VALUES_REQUIRED_ERROR_MESSAGE =
  'Values are required for the selected operator';

export const SELECTOR_CRITERIA_REQUIRED_ERROR_MESSAGE =
  'At least one selector criterion is required';

export const MIN_REPLICAS_ERROR_MESSAGE = `Min replicas must be at least ${MIN_REPLICAS}`;

export const KEY_AND_VALUES_REQUIRED_ERROR_MESSAGE =
  'Key and values are required';

export const KEY_REQUIRED_ERROR_MESSAGE = 'Key is required';

export const RULE_TYPE_REQUIRED_ERROR_MESSAGE = 'Rule type is required';
